/**
 * SuccessDialog Component
 * 
 * Reusable success dialog for showing completion messages after:
 * - Membership upgrades
 * - Credit purchases
 * - Reward claims
 * - Any other successful action
 */

import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './dialog'
import { Button } from './button'
import { CheckCircle, Gift, CreditCard, Crown } from 'lucide-react'

export type SuccessDialogVariant = 'default' | 'reward' | 'credits' | 'membership'

interface SuccessDialogProps {
  open: boolean
  onClose: () => void
  variant?: SuccessDialogVariant
  title: string
  description?: string
  details?: React.ReactNode
}

export function SuccessDialog({
  open,
  onClose,
  variant = 'default',
  title,
  description,
  details,
}: SuccessDialogProps) {
  const { t } = useTranslation()

  const getIcon = () => {
    const iconClass = 'h-10 w-10'
    switch (variant) {
      case 'reward':
        return <Gift className={`${iconClass} text-emerald-600`} />
      case 'credits':
        return <CreditCard className={`${iconClass} text-green-600`} />
      case 'membership':
        return <Crown className={`${iconClass} text-yellow-500`} />
      default:
        return <CheckCircle className={`${iconClass} text-green-600`} />
    }
  }

  const getIconBgClass = () => {
    switch (variant) {
      case 'reward':
        return 'bg-emerald-100 dark:bg-emerald-900/30'
      case 'credits':
        return 'bg-green-100 dark:bg-green-900/30'
      case 'membership':
        return 'bg-yellow-100 dark:bg-yellow-900/30'
      default:
        return 'bg-green-100 dark:bg-green-900/30'
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="success-dialog sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription>{description}</DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex flex-col items-center justify-center py-6 space-y-4">
            <div className={`w-16 h-16 rounded-full ${getIconBgClass()} flex items-center justify-center`}>
              {getIcon()}
            </div>
            
            {details && (
              <div className="w-full">
                {details}
              </div>
            )}
          </div>

          <Button onClick={onClose} className="w-full">
            {t('common.continue', 'Continue')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
